import React from 'react';
import Numeral from 'numeral';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Paper,
  styled,
  Typography as TypographyBase,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { FormProvider, useForm, type FieldValues } from 'react-hook-form';
import { useQuoteCreationFunctions } from '../../../accounts/accountUtils';

// components
import CbButton from '../../Buttons/CbButton';
import {
  ProductTypes,
  type AccountDto,
  type ProductType,
} from '../../../types';

// services
import {
  bulkRequestQuotesFromCart,
  deleteQuoteFromCartList,
} from '../../../api';
import { useGetQuoteCartList } from '../../../console/agencies/quotes/PrimeX/hooks/useGetQuoteCartList';
import { useOptimisticMutation } from '../../hooks/tanstack-query/useOptimisticUpdate';
import { manageAPIError } from '../../../utils';
import type { GetQuoteCartListResponse } from '../../../api/quotes.api.types';
import { toUniversalUtcDate } from '../../../utils/date.utils';
import {
  getCurrencySymbolByProduct,
  getRegionByProduct,
} from '../../../i18n/utils';
import { useCowbellTranslations } from '../../../i18n/translations';
import { usePersona } from '../../hooks/usePersona';
import { PERSONA_TYPES } from '../../../console/_statics/persona.statics';
import { Show } from '../../Show';
import { deriveCurrencyFormat } from '../../../i18n/currencies';
import { AGENCY_DASHBOARD_LISTING_TABS } from '../../../console/dashboard/agency/agency-dashboard.statics';
import { LIMIT_CURRENCY_FORMAT } from '../../../utils/currency.utils';
import type { PrimeXQuoteHandlers } from '../../../console/workflows/quotes/prime-x/types';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { PubSub } from '../../../utils/eventUtils';
import { SubmissionDetailsDto } from '../../../api/submissions/submissions.api.types';
import {
  toQuoteOptionRow,
  type QuoteOptionRow,
} from '../../../console/workflows/quotes/shared/quote-options/optionCartRow';
import { renewalFieldsOf } from '../../../console/workflows/quotes/shared/quote-options/optionTerms';
import {
  cartOptionsFor,
  useQuoteCartVisit,
} from '../../../console/workflows/quotes/shared/quote-options/quoteCartVisit';
import { useQuoteOptions } from '../../../console/workflows/quotes/shared/quote-options/useQuoteOptions';
import { useRemoveQuoteOption } from '../../../console/workflows/quotes/shared/quote-options/useRemoveQuoteOption';
import { useSubmitQuoteOptions } from '../../../console/workflows/quotes/shared/quote-options/useSubmitQuoteOptions';

interface QuoteCartProps {
  data: {
    accountData?: AccountDto;
    accountId: string;
    productType: ProductType;
    uiSettings?: Record<string, boolean>;
    setCartCount?: PrimeXQuoteHandlers['setCartCount'];
    isRenewal?: boolean;
    submissionId?: string;
    submission?: SubmissionDetailsDto;
    onSubmitSuccess?: () => void;
  };
  close: () => void;
}

/**
 * A submission quotes its coverage options: Add to Cart saves one and this lists them. Without a
 * submission the flow is account-keyed and still runs on the cart.
 */
const QuoteCartModal = ({ data: modalProps, close }: QuoteCartProps) => {
  if (modalProps.submissionId) {
    return (
      <QuoteOptionsCart
        data={{ ...modalProps, submissionId: modalProps.submissionId }}
        close={close}
      />
    );
  }
  return <AccountQuoteCart data={modalProps} close={close} />;
};

//TODO: This modal needs to be re-done for rater x. Its adding too much bloat
const AccountQuoteCart = ({ data: modalProps, close }: QuoteCartProps) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const formMethods = useForm({
    defaultValues: {
      renewalPolicyNumber: modalProps.submission?.expiringPolicyNumber ?? '',
      renewalPolicyId: modalProps.submission?.expiringPolicyId ?? '',
      isRenewal: modalProps.isRenewal ?? false,
    },
  });

  const history = useHistory();
  const { type } = usePersona();
  const { accountId, productType, accountData, uiSettings } = modalProps;
  const pathMapping = usePathMapping(modalProps.accountId);

  const { detemineAgentOnQuoteCreation, getAgencyId, determineTeamIdOfAgent } =
    useQuoteCreationFunctions(accountData);
  const notifications = useNotifications();

  const removeQuoteFromCartMutation =
    useOptimisticMutation<GetQuoteCartListResponse>()({
      queryKey: ['request-quote-cart', { accountId, productType }],
      mutationFn: (quoteId: string) => {
        return deleteQuoteFromCartList(productType, quoteId, {
          params: getAgencyId() ? { agencyId: getAgencyId() } : {},
        });
      },
      syncCacheUpdate: (previousCache, quoteId) => {
        modalProps.setCartCount?.(previousCache.count - 1);
        return {
          ...previousCache,
          quoteList: previousCache?.quoteList?.filter(
            (quote) => quote.id !== quoteId
          ),
          count: previousCache.count - 1,
        };
      },
      onSuccess: ({ data }) => {
        notifications.show({
          type: 'success',
          title: `Successfully removed quote ${data.quote?.quoteNumber}`,
        });
      },
      onError: (_err, _quoteId, previousCache) => {
        modalProps.setCartCount?.(previousCache?.previousState?.count ?? 0);
        notifications.show({
          type: 'error',
          title: 'Failed to remove quote, please try again',
        });
      },
    });
  const { data: cartData, isInitialLoading } = useGetQuoteCartList({
    accountData: modalProps.accountData,
    accountId: modalProps.accountId,
    productType: modalProps.productType,
  });

  const handleRequestQuotes = (fieldValues: FieldValues) => {
    setIsLoading(true);

    bulkRequestQuotesFromCart(productType, accountId, {
      params: getAgencyId() ? { agencyId: getAgencyId() } : {},
      data: {
        ...detemineAgentOnQuoteCreation(),
        teamId: determineTeamIdOfAgent()[0],
        renewalPolicyNumber: fieldValues.renewalPolicyNumber,
        renewalPolicyId: fieldValues.renewalPolicyId,
        isRenewal: fieldValues.isRenewal,
      },
    })
      .then(() => {
        notifications.show({
          type: 'success',
          title: 'Quotes requested successfully',
        });
        close();

        if (modalProps.onSubmitSuccess != null) {
          modalProps.onSubmitSuccess();
        } else {
          setTimeout(() => {
            history.push(pathMapping[type as keyof typeof pathMapping]);
          }, 800);

          setTimeout(() => {
            PubSub.publish('quote-refresh:show', true);
          }, 10 * 1000);
        }
      })
      .catch((error: AxiosError) => {
        notifications.show({
          type: 'error',
          title: 'Unable to request quotes at this time',
          body: manageAPIError(error),
        });
      })
      .finally(() => setIsLoading(false));
  };

  if (isInitialLoading) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(handleRequestQuotes)} noValidate>
        <DialogContent style={{ padding: '2rem' }}>
          {cartData?.count === 0 ? (
            <Box
              className="primer-text contrast-text"
              display="flex"
              alignItems="center"
              justifyContent="center"
              padding="2rem 0"
            >
              No quotes have been added to the cart yet.
            </Box>
          ) : (
            <QuoteCartTable
              rows={cartData?.quoteList ?? []}
              productType={productType}
              uiSettings={uiSettings}
              unpricedLabel="Referred"
              onDelete={(quoteId) =>
                removeQuoteFromCartMutation.mutate(quoteId)
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <CbButton styleName="cancel" size="medium" onClick={close}>
            Cancel
          </CbButton>
          <CbButton
            disabled={cartData?.count === 0 || isLoading}
            styleName="ctaButton"
            type="submit"
            size="medium"
            loading={isLoading}
          >
            Request Above Quotes
          </CbButton>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

type QuoteCartTableProps = {
  readonly rows: readonly QuoteOptionRow[];
  readonly productType: ProductType;
  readonly uiSettings?: Record<string, boolean>;
  /** Shown in place of a price when a row has none. */
  readonly unpricedLabel: string;
  /** A saved option has no quote number yet, so the options cart leaves the column out. */
  readonly showQuoteNumber?: boolean;
  readonly onDelete: (rowId: string) => void;
};

const QuoteCartTable = ({
  rows,
  productType,
  uiSettings,
  unpricedLabel,
  showQuoteNumber = true,
  onDelete,
}: QuoteCartTableProps) => {
  const currencySymbol = getCurrencySymbolByProduct(productType);
  const region = getRegionByProduct(productType);
  const { t } = useCowbellTranslations();
  const quoteNumberWidth = uiSettings?.renderCommissionSlider ? '20%' : '30%';
  // Without the quote number column the preset takes its width, so the row still fills the table.
  const presetWidth = showQuoteNumber
    ? '10%'
    : `${10 + parseInt(quoteNumberWidth, 10)}%`;

  return (
    <Paper elevation={3} style={{ borderRadius: '0.5rem', overflow: 'hidden' }}>
      <HeaderContainer className="contrast-text">
        {showQuoteNumber && (
          <Typography variant="subtitle2" style={{ width: quoteNumberWidth }}>
            Quote Number
          </Typography>
        )}
        <Typography variant="subtitle2" style={{ width: presetWidth }}>
          Coverage Selection
        </Typography>
        {productType !== ProductTypes.zsp && (
          <>
            <Typography variant="subtitle2" style={{ width: '10%' }}>
              Aggregate Limit
            </Typography>
            <Typography variant="subtitle2" style={{ width: '10%' }}>
              {t('retention')} Amount
            </Typography>
          </>
        )}
        <Typography variant="subtitle2" style={{ width: '12%' }}>
          Indicated <br /> Pricing
        </Typography>
        <Typography variant="subtitle2" style={{ width: '15%' }}>
          Coverage <br /> Start Date
        </Typography>

        <Show when={!!uiSettings?.renderCommissionSlider}>
          <Typography variant="subtitle2" style={{ width: '10%' }}>
            Commission
          </Typography>
        </Show>
        <Typography
          variant="subtitle2"
          style={{ width: '8%', textAlign: 'center' }}
        >
          Delete
        </Typography>
      </HeaderContainer>
      {rows.map((row) => (
        <Container key={row.id}>
          {showQuoteNumber && (
            <Typography variant="body2" style={{ width: quoteNumberWidth }}>
              {row.quoteNumber}
            </Typography>
          )}
          <Typography style={{ width: presetWidth }}>{row.presets}</Typography>
          {productType !== ProductTypes.zsp && (
            <>
              <Typography variant="body2" style={{ width: '10%' }}>
                {currencySymbol}
                {Numeral(row.aggLimit).format(LIMIT_CURRENCY_FORMAT)}
              </Typography>
              <Typography variant="body2" style={{ width: '10%' }}>
                {currencySymbol}
                {Numeral(row.deductible).format(`0,0`)}
              </Typography>
            </>
          )}

          <Typography variant="body2" style={{ width: '12%' }}>
            {deriveCurrencyFormat(region, row.totalPremium, {
              placeholder: unpricedLabel,
            })}
          </Typography>
          <Typography variant="body2" style={{ width: '15%' }}>
            {toUniversalUtcDate(row.effectiveDate)}
          </Typography>

          <Show when={!!uiSettings?.renderCommissionSlider}>
            <Typography
              variant="subtitle2"
              style={{ width: '10%', paddingLeft: '1rem' }}
            >
              {row.commissionRate}%
            </Typography>
          </Show>

          <Typography
            variant="body2"
            style={{ width: '8%', textAlign: 'center' }}
          >
            <Button
              style={{ padding: 0, marginRight: '-0.6rem' }}
              color="primary"
              size="small"
              variant="text"
              onClick={() => onDelete(row.id)}
            >
              Delete
            </Button>
          </Typography>
        </Container>
      ))}
    </Paper>
  );
};

type QuoteOptionsCartProps = {
  readonly data: QuoteCartProps['data'] & { readonly submissionId: string };
  readonly close: () => void;
};

/** The cart of a submission: its unquoted coverage options of this product. */
const QuoteOptionsCart = ({
  data: modalProps,
  close,
}: QuoteOptionsCartProps) => {
  const { submissionId, productType, uiSettings, accountData } = modalProps;
  const { detemineAgentOnQuoteCreation, getAgencyId, determineTeamIdOfAgent } =
    useQuoteCreationFunctions(accountData);
  const history = useHistory();
  const { type } = usePersona();
  const notifications = useNotifications();
  const pathMapping = usePathMapping(modalProps.accountId);

  const optionsQuery = useQuoteOptions(submissionId);

  const visitIds = useQuoteCartVisit(submissionId);
  const unquoted = cartOptionsFor(
    productType,
    optionsQuery.data?.coverageOptions ?? [],
    visitIds
  );
  const removeOption = useRemoveQuoteOption(submissionId);
  const { submit, isLoading } = useSubmitQuoteOptions(submissionId);

  const handleDelete = (optionId: string) => {
    removeOption.mutate(optionId, {
      onSuccess: () =>
        notifications.show({
          type: 'success',
          title: 'Removed coverage option',
        }),
      onError: () =>
        notifications.show({
          type: 'error',
          title: 'Failed to remove coverage option, please try again',
        }),
    });
  };

  const handleRequestQuotes = async () => {
    // The same agent, team, agency and renewal fields the cart submit sent.
    const created = await submit(
      unquoted.map((option) => option.id),
      {
        ...detemineAgentOnQuoteCreation(),
        teamId: determineTeamIdOfAgent()[0],
        ...renewalFieldsOf(modalProps.submission),
        // The request view's renewal toggle is not on the submission yet, so a toggle that was set wins.
        ...(modalProps.isRenewal != null && {
          isRenewal: modalProps.isRenewal,
        }),
        agencyId: getAgencyId(),
      }
    );
    if (created === 0) {
      return;
    }
    close();

    if (modalProps.onSubmitSuccess != null) {
      modalProps.onSubmitSuccess();
      return;
    }
    setTimeout(() => {
      history.push(pathMapping[type as keyof typeof pathMapping]);
    }, 800);
    setTimeout(() => {
      PubSub.publish('quote-refresh:show', true);
    }, 10 * 1000);
  };

  if (optionsQuery.isInitialLoading) {
    return null;
  }

  return (
    <>
      <DialogContent style={{ padding: '2rem' }}>
        {unquoted.length === 0 ? (
          <Box
            className="primer-text contrast-text"
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding="2rem 0"
          >
            No coverage options have been saved yet.
          </Box>
        ) : (
          <QuoteCartTable
            rows={unquoted.map(toQuoteOptionRow)}
            productType={productType}
            uiSettings={uiSettings}
            unpricedLabel="Not priced"
            showQuoteNumber={false}
            onDelete={handleDelete}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CbButton styleName="cancel" size="medium" onClick={close}>
          Cancel
        </CbButton>
        <CbButton
          disabled={unquoted.length === 0 || isLoading}
          styleName="ctaButton"
          size="medium"
          loading={isLoading}
          onClick={handleRequestQuotes}
        >
          Request Above Quotes
        </CbButton>
      </DialogActions>
    </>
  );
};

const Container = styled(Box)(({ theme }: { theme: any }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '0.65rem',
  padding: '0.65rem',
  '&:nth-child(odd)': {
    backgroundColor: theme.palette.background.oddRow,
  },
}));

const HeaderContainer = styled(Container)(() => ({
  backgroundColor: 'red',
  alignItems: 'center',
}));

const Typography = styled(TypographyBase)(() => ({
  lineHeight: 1.25,
}));

export const QuoteCartModalConfig = {
  QuoteCartModal: {
    component: QuoteCartModal,
    config: {
      title: 'List of Quotes to Request',
      fullWidth: true,
      maxWidth: 'md',
    },
  },
};

const usePathMapping = (accountId: string) => ({
  [PERSONA_TYPES.COWBELL]: '/admin/quotes',
  [PERSONA_TYPES.AGENCY]: `/agency/dashboard?tab=${AGENCY_DASHBOARD_LISTING_TABS.QUOTES}&prefilterAccountId=${accountId}`,
  [PERSONA_TYPES.CARRIER]: '/carrier/quotes',
});
